import React, { type HTMLAttributes, useCallback, useState } from 'react';

import { MiniTooltip } from '#@/components/Tooltip';

import { TruncatedText, Wrapper } from './Styles';

export interface HoverableTextProps extends HTMLAttributes<HTMLDivElement> {
  text?: React.ReactNode | string | null;
  textWhenNotTruncated?: React.ReactNode | string | null;
  multiline?: boolean;
}

const HoverableText = ({
  text,
  textWhenNotTruncated,
  multiline,
  ...restProps
}: HoverableTextProps) => {
  const [node, setNode] = useState<HTMLElement>();
  const textRef = useCallback((textNode: HTMLElement | null) => {
    if (textNode !== null) {
      setNode(textNode);
    }
  }, []);

  const [textToDisplay, setTextToDisplay] = useState(textWhenNotTruncated);

  const checkElementSizes = useCallback(() => {
    const textWidth = node?.getBoundingClientRect()?.width;
    const parentWidth = node?.parentElement?.getBoundingClientRect()?.width;

    const isTextTruncated = textWidth && parentWidth && textWidth > parentWidth;
    if (isTextTruncated) {
      setTextToDisplay(text);
    } else {
      setTextToDisplay(textWhenNotTruncated);
    }
  }, [node, text, textWhenNotTruncated]);

  return (
    <MiniTooltip<HTMLDivElement> bodyText={textToDisplay} isTriggerElementClickable={false}>
      {({ ref, getReferenceProps }) => (
        <Wrapper
          ref={ref}
          {...restProps}
          {...getReferenceProps({
            onMouseEnter: () => checkElementSizes(),
          })}
        >
          <TruncatedText $multiline={multiline}>
            <span ref={textRef}>{text}</span>
          </TruncatedText>
        </Wrapper>
      )}
    </MiniTooltip>
  );
};

export default HoverableText;
